import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { paymentsAPI } from "@/lib/api";
import { Gauge, AlertTriangle, X, Lock, Zap, ArrowRight, Settings } from "lucide-react";
import { toast } from "sonner";

const TXT = {
    fr: {
        used: "Shorts utilisés",
        left_one: "Il te reste {n} Short ce mois-ci.",
        left_many: "Il te reste {n} Shorts ce mois-ci.",
        plan_free: "Aucune formule active — choisis une offre pour lancer ta production.",
        plan_oneshot: "Formule One Shot · 1 Short inclus",
        plan_creator: "Formule Creator · {limit} Shorts/mois",
        reset: "Réinitialisation le 1er du mois prochain.",
        modal_title_oneshot: "Ton Short unique a été livré.",
        modal_title_creator: "Capacité mensuelle atteinte.",
        modal_title_free: "Aucun Short disponible.",
        modal_sub_oneshot: "Passe à Creator pour continuer à produire — 30 Shorts par mois inclus, empilable +30 par pack.",
        modal_sub_creator: "Ajoute un pack +30 Shorts pour 19€/mois, ou attends le renouvellement du mois prochain.",
        modal_sub_free: "Choisis une formule pour débloquer la production.",
        cta_upgrade: "Passer à Creator · 39 €/mois",
        cta_buy_unique: "One Shot · 9 €",
        cta_add_pack: "Ajouter un pack · +19 €/mois",
        cta_close_monthly: "Voir ma bibliothèque",
        close: "Fermer",
        manage: "Gérer mon abonnement",
    },
    en: {
        used: "Shorts used",
        left_one: "You have {n} Short left this month.",
        left_many: "You have {n} Shorts left this month.",
        plan_free: "No active plan — pick an offer to start producing.",
        plan_oneshot: "One Shot plan · 1 Short included",
        plan_creator: "Creator plan · {limit} Shorts/month",
        reset: "Resets on the 1st of next month.",
        modal_title_oneshot: "Your single Short has been delivered.",
        modal_title_creator: "Monthly capacity reached.",
        modal_title_free: "No Short available.",
        modal_sub_oneshot: "Upgrade to Creator to keep producing — 30 Shorts per month included, stackable +30 per pack.",
        modal_sub_creator: "Add a +30 Shorts pack for €19/month, or wait for next month's renewal.",
        modal_sub_free: "Pick a plan to unlock production.",
        cta_upgrade: "Upgrade to Creator · €39/mo",
        cta_buy_unique: "One Shot · €9",
        cta_add_pack: "Add a pack · +€19/mo",
        cta_close_monthly: "Open my library",
        close: "Close",
        manage: "Manage my subscription",
    },
    es: {
        used: "Shorts usados",
        left_one: "Te queda {n} Short este mes.",
        left_many: "Te quedan {n} Shorts este mes.",
        plan_free: "Sin plan activo — elige una oferta para empezar a producir.",
        plan_oneshot: "Plan One Shot · 1 Short incluido",
        plan_creator: "Plan Creator · {limit} Shorts/mes",
        reset: "Reinicio el 1 del mes siguiente.",
        modal_title_oneshot: "Tu Short único ha sido entregado.",
        modal_title_creator: "Capacidad mensual alcanzada.",
        modal_title_free: "Ningún Short disponible.",
        modal_sub_oneshot: "Pásate a Creator para seguir produciendo — 30 Shorts al mes incluidos, apilables +30 por pack.",
        modal_sub_creator: "Añade un pack +30 Shorts por 19€/mes, o espera la renovación del próximo mes.",
        modal_sub_free: "Elige un plan para desbloquear la producción.",
        cta_upgrade: "Pasar a Creator · 39 €/mes",
        cta_buy_unique: "One Shot · 9 €",
        cta_add_pack: "Añadir un pack · +19 €/mes",
        cta_close_monthly: "Ver mi biblioteca",
        close: "Cerrar",
        manage: "Gestionar mi suscripción",
    },
};

export default function QuotaBanner({ onOpenLibrary }) {
    const { lang, quota, refreshQuota } = useApp();
    const navigate = useNavigate();
    const t = TXT[lang] || TXT.fr;
    const [modalOpen, setModalOpen] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    // Refresh quota periodically to reflect consumption from modules
    useEffect(() => {
        refreshQuota();
        const id = setInterval(() => refreshQuota(), 20000);
        const onFocus = () => refreshQuota();
        window.addEventListener("focus", onFocus);
        window.addEventListener("sf:quota-changed", onFocus);
        return () => {
            clearInterval(id);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("sf:quota-changed", onFocus);
        };
    }, [refreshQuota]);

    const isBlocked = useMemo(() => !!quota && quota.remaining <= 0, [quota]);

    // Auto-open blocking modal when user hits 0 (and hasn't dismissed it this session)
    useEffect(() => {
        if (isBlocked && !dismissed) setModalOpen(true);
    }, [isBlocked, dismissed]);

    if (!quota) return null;

    const { plan, used, limit, remaining } = quota;
    const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const leftText = (remaining === 1 ? t.left_one : t.left_many).replace("{n}", remaining);

    const planLabel =
        plan === "creator" ? (t.plan_creator || "").replace("{limit}", limit) :
        plan === "oneshot" ? t.plan_oneshot :
                             t.plan_free;

    const barColor =
        percent >= 100 ? "bg-red-500" :
        percent >= 80  ? "bg-[#FFB800]" :
                         "bg-[#FF4D00]";

    const buyPlan = async (planId, units = 1) => {
        try {
            const { checkout_url } = await paymentsAPI.checkout(planId, units);
            window.location.href = checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Checkout error");
        }
    };

    const openPortal = async () => {
        try {
            const { portal_url } = await paymentsAPI.portal();
            window.location.href = portal_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Portal error");
        }
    };

    return (
        <>
            <div
                data-testid="quota-banner"
                className={`mb-6 rounded-lg border ${isBlocked ? "border-red-500/50" : "border-[#253046]"} bg-gradient-to-r from-[#0F141C] via-[#0B111B] to-[#0F141C] p-4 sm:p-5`}
            >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-3 shrink-0">
                        <div className={`w-10 h-10 rounded-lg grid place-items-center ${isBlocked ? "bg-red-500/15 text-red-400" : "bg-[#FF4D00]/15 text-[#FF4D00]"}`}>
                            {isBlocked ? <Lock className="w-5 h-5" /> : <Gauge className="w-5 h-5" />}
                        </div>
                        <div>
                            <div className="overline text-[10px]">{planLabel}</div>
                            <div className="font-display text-base sm:text-lg font-bold uppercase leading-tight">
                                <span data-testid="quota-used">{t.used} : {used} / {limit}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="w-full h-2 rounded-full bg-[#161D2A] overflow-hidden">
                            <div
                                data-testid="quota-progress"
                                className={`h-full ${barColor} transition-all duration-500`}
                                style={{ width: `${percent}%` }}
                            />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs font-mono-sf text-slate-400">
                            <span data-testid="quota-remaining">
                                {isBlocked ? (
                                    <span className="text-red-400 inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> 0/{limit}</span>
                                ) : leftText}
                            </span>
                            {plan === "creator" && <span className="text-slate-500">{t.reset}</span>}
                        </div>
                    </div>

                    {plan === "creator" && (
                        <button
                            data-testid="manage-subscription-btn"
                            onClick={openPortal}
                            className="shrink-0 bg-[#0F141C] border border-[#253046] hover:border-[#FF4D00] text-white font-semibold px-4 py-2.5 rounded-md text-sm kinetic flex items-center gap-2"
                        >
                            <Settings className="w-4 h-4" /> {t.manage}
                        </button>
                    )}

                    {isBlocked && (
                        <button
                            data-testid="quota-upgrade-cta"
                            onClick={() => setModalOpen(true)}
                            className="shrink-0 flame-gradient text-black font-semibold px-4 py-2.5 rounded-md text-sm kinetic flex items-center gap-2"
                        >
                            <Zap className="w-4 h-4" fill="currentColor" />
                            {plan === "oneshot" ? t.cta_upgrade : plan === "creator" ? t.cta_add_pack : t.cta_buy_unique}
                        </button>
                    )}
                </div>
            </div>

            {modalOpen && isBlocked && (
                <div
                    data-testid="quota-block-modal"
                    className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm grid place-items-center p-4"
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="relative max-w-md w-full rounded-2xl border-2 border-[#FF4D00]/60 bg-gradient-to-b from-[#1E2738] via-[#161D2A] to-[#0B111B] p-6 sm:p-8 flame-glow">
                        <button
                            data-testid="quota-modal-close"
                            onClick={() => { setModalOpen(false); setDismissed(true); }}
                            className="absolute top-3 right-3 text-slate-400 hover:text-white"
                            aria-label={t.close}
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="w-14 h-14 rounded-full grid place-items-center bg-red-500/15 text-red-400 mb-4">
                            <Lock className="w-7 h-7" />
                        </div>

                        <h2 className="font-display text-2xl sm:text-3xl font-black uppercase leading-tight">
                            {plan === "creator" ? t.modal_title_creator : plan === "oneshot" ? t.modal_title_oneshot : t.modal_title_free}
                        </h2>
                        <p className="mt-3 text-slate-300 leading-relaxed">
                            {plan === "creator" ? t.modal_sub_creator : plan === "oneshot" ? t.modal_sub_oneshot : t.modal_sub_free}
                        </p>

                        <div className="mt-6 flex flex-col gap-2">
                            {plan === "creator" ? (
                                <>
                                    <button
                                        data-testid="quota-modal-add-pack"
                                        onClick={() => buyPlan("creator", Math.max(2, (quota?.units || 1) + 1))}
                                        className="w-full py-3 rounded-md font-semibold flame-gradient text-black kinetic flex items-center justify-center gap-2"
                                    >
                                        <Zap className="w-4 h-4" fill="currentColor" /> {t.cta_add_pack}
                                    </button>
                                    <button
                                        data-testid="quota-modal-library"
                                        onClick={() => { setModalOpen(false); setDismissed(true); onOpenLibrary?.(); }}
                                        className="w-full py-2.5 rounded-md font-semibold bg-[#0F141C] border border-[#253046] hover:border-[#FF4D00] text-white kinetic flex items-center justify-center gap-2"
                                    >
                                        {t.cta_close_monthly} <ArrowRight className="w-4 h-4" />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        data-testid="quota-modal-upgrade"
                                        onClick={() => buyPlan("creator", 1)}
                                        className="w-full py-3 rounded-md font-semibold flame-gradient text-black kinetic flex items-center justify-center gap-2"
                                    >
                                        <Zap className="w-4 h-4" fill="currentColor" /> {t.cta_upgrade}
                                    </button>
                                    {plan === "free" && (
                                        <button
                                            data-testid="quota-modal-buy-unique"
                                            onClick={() => buyPlan("oneshot")}
                                            className="w-full py-2.5 rounded-md font-semibold bg-[#0F141C] border border-[#253046] hover:border-[#FF4D00] text-white kinetic"
                                        >
                                            {t.cta_buy_unique}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
